import { Controller } from "@hotwired/stimulus";

/**
 * The widget gallery's two client-side jobs.
 *
 * `filter` narrows a list of options — gallery tiles on step 1, workflows on
 * step 2 — against a search box, matching the text the server put in
 * `data-search` rather than the rendered text, so a match never depends on
 * markup.
 *
 * `updatePreview` points the preview frame at the display the form currently
 * holds. The preview is a sample card at that display's starting footprint, so
 * nothing else on the form reaches it.
 *
 * Connects to data-controller="widget-configure"
 */
export default class extends Controller {
  static targets = ["search", "option", "empty", "preview"];

  declare readonly hasSearchTarget: boolean;
  declare readonly searchTarget: HTMLInputElement;
  declare readonly optionTargets: HTMLElement[];
  declare readonly hasEmptyTarget: boolean;
  declare readonly emptyTarget: HTMLElement;
  declare readonly previewTargets: HTMLElement[];

  filter() {
    if (!this.hasSearchTarget) return;

    const query = this.searchTarget.value.toLowerCase().trim();
    let matches = 0;

    this.optionTargets.forEach((option) => {
      const haystack = option.dataset.search || option.textContent?.toLowerCase() || "";
      const matched = haystack.includes(query);
      option.classList.toggle("hidden", !matched);
      if (matched) matches += 1;
    });

    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", matches > 0);
  }

  updatePreview() {
    const display = this.chosenDisplay();

    this.previewTargets.forEach((frame) => {
      const base = frame.dataset.previewUrl;
      if (!base || !display) return;

      const url = new URL(base, window.location.origin);
      url.searchParams.set("display", display);
      frame.setAttribute("src", url.pathname + url.search);
    });
  }

  // The display the form currently holds, read off the form so the picker can be
  // any control that submits widget[config][display].
  private chosenDisplay(): string | null {
    if (!(this.element instanceof HTMLFormElement)) return null;

    const display = new FormData(this.element).get("widget[config][display]");

    return display ? String(display) : null;
  }
}
